<template>
  <v-container fluid fill-height>
    <v-row justify="space-around" class="justify-center">
      <v-col cols="12" sm="6" md="6" lg="6" align-self="center" class="text-center mb-12">
        <v-img class="d-inline-block" width="80%" :src="SinodaLogo" />
      </v-col>
      <v-col cols="12" sm="6" md="6" lg="6" class="text-center">
        <v-card class="width-login d-inline-block" rounded>
          <v-card-title class="text-h5 font-weight-regular primary white--text">
            Login
          </v-card-title>
          <v-form ref="form" v-model="form" @submit.prevent="submit">
            <v-card-text class="mt-5 pb-0">
              <v-text-field
                label="Email"
                placeholder="Enter Email Address"
                :rules="[rules.email]"
                outlined
                v-model="loginForm.email"
                prepend-inner-icon="mdi-account"
                clearable
              ></v-text-field>
              <v-text-field
                label="Password"
                placeholder="Enter Password"
                :rules="[rules.required, rules.password]"
                outlined
                counter="15"
                v-model="loginForm.password"
                prepend-inner-icon="mdi-key"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="showPassword = !showPassword"
                :type="showPassword ? 'text' : 'password'"
                clearable
              ></v-text-field>
            </v-card-text>
            <v-card-actions class="justify-center">
              <v-btn color="primary" type="submit"
                :loading="loading" :disabled="loading" @click="loginClick">Submit</v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>

    <!-- 2FA Setup Dialog -->
    <v-dialog v-model="tfaSetupDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h5 primary white--text">
          Setup Two-Factor Authentication
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="mb-2">
            <strong>Step 1:</strong> Download "Google Authenticator" from the App Store or Play Store if you don't have it yet.
          </p>
          <p class="mb-4">
            <strong>Step 2:</strong> Scan this QR code with your authenticator app or click QR Code to add to Google Authenticator:
          </p>
          <div class="text-center">
            <a :href="tfaSetupUri">
              <qrcode
                :text="tfaSetupUri"
                size="150"
                color="#000000"
                class="d-flex justify-center"
                bg-color="#ffffff" 
                error-level="L">
              </qrcode>
            </a>
          </div>
            
          <!-- Manual Token Copy Section -->
          <div v-if="tfaToken" class="mt-4">
            <p class="text-caption mb-2">Or manually add this secret key to your authenticator app:</p>
            <v-text-field
              :value="tfaToken"
              readonly
              outlined
              dense
              append-icon="mdi-content-copy"
              @click:append="copyTokenToClipboard"
              class="mb-2"
              hint="Click the copy icon to copy the secret key"
              persistent-hint
            ></v-text-field>
          </div>
          
          <p class="mt-4 mb-2"><strong>Step 3:</strong> Enter the 6-digit code from your authenticator app:</p>
          <v-text-field
            v-model="tfaOtp"
            label="6-digit OTP"
            placeholder="000000"
            outlined
            maxlength="6"
            counter="6"
            :rules="[rules.required, rules.otp]"
            prepend-inner-icon="mdi-shield-key"
            autofocus
            ref="tfaSetupOtpInput"
            @keyup.enter="verifyTfaSetup"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="verifyTfaSetup" :loading="tfaLoading">
            Verify OTP
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 2FA Verification Dialog -->
    <v-dialog v-model="tfaVerificationDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-h5 primary white--text">
          Two-Factor Authentication
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="mb-4">Enter the 6-digit code from your authenticator app:</p>
          <v-text-field
            v-model="tfaOtp"
            label="6-digit OTP"
            placeholder="000000"
            outlined
            maxlength="6"
            counter="6"
            :rules="[rules.required, rules.otp]"
            prepend-inner-icon="mdi-shield-key"
            autofocus
            ref="tfaVerificationOtpInput"
            @keyup.enter="verifyTfaOtp"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="verifyTfaOtp" :loading="tfaLoading">
            Verify OTP
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import rules from '../../utilities/validator';
  import SinodaLogo from 'images/SinodaLogo.png';
  import VueQRCodeComponent from 'vue-qrcode-component';

  export default {
    name: 's-login-page',
    components: {
      qrcode: VueQRCodeComponent
    },
    data() {
      return {
        showPassword: false,
        SinodaLogo,
        rules,
        form: false,
        loginForm: {
          email: '',
          password: '',
        },
        loading: false,
        tfaSetupDialog: false,
        tfaVerificationDialog: false,
        tfaOtp: '',
        tfaSetupUri: '',
        tfaToken: '',
        tfaLoading: false,
        currentUserId: null,
      };
    },
    methods: {
      submit() {
        return this.$refs.form.validate();
      },
      loginClick() {
        if(!this.submit()) return;
        const self = this;
        this.loading = true;
        this.$api.sessions.login(this.loginForm)
          .then((response) => {
            this.loading = false;
            
            // Check if 2FA setup is required (admin without 2FA enabled)
            if (response.tfa_setup_uri) {
              this.tfaSetupUri = response.tfa_setup_uri;
              this.tfaToken = response.tfa_token;
              this.tfaSetupDialog = true;
              this.currentUserId = response.user_id;
              this.$nextTick(() => {
                this.focusOtpInput('tfaSetupOtpInput');
              });
              return;
            }
            
            // Check if 2FA verification is required (user with 2FA enabled)
            if (response.is_tfa_enabled) {
              this.tfaVerificationDialog = true;
              this.currentUserId = response.user_id;
              this.$nextTick(() => {
                this.focusOtpInput('tfaVerificationOtpInput');
              });
              return;
            }
            
            // Normal login flow
            this.handleSuccessfulLogin(response);
          }).catch((err) => {
            this.loading = false;
            this.$store.dispatch('setAlert', {
              status: 'error',
              show: true,
              message: err.message,
            });
          });
      },
      verifyTfaSetup() {
        if (!this.tfaOtp || this.tfaOtp.length !== 6) {
          this.$store.dispatch('setAlert', {
            status: 'error',
            show: true,
            message: 'Please enter a valid 6-digit OTP',
          });
          return;
        }
        
        this.tfaLoading = true;
        const loginData = {
          ...this.loginForm,
          tfa_otp: this.tfaOtp
        };
        
        this.$api.sessions.login(loginData)
          .then((response) => {
            this.tfaLoading = false;
            this.tfaSetupDialog = false;
            this.tfaOtp = '';
            this.tfaToken = '';
            this.tfaSetupUri = '';
            this.handleSuccessfulLogin(response, '2FA setup completed successfully!');
          }).catch((err) => {
            this.tfaLoading = false;
            this.$store.dispatch('setAlert', {
              status: 'error',
              show: true,
              message: err.message,
            });
          });
      },
      verifyTfaOtp() {
        if (!this.tfaOtp || this.tfaOtp.length !== 6) {
          this.$store.dispatch('setAlert', {
            status: 'error',
            show: true,
            message: 'Please enter a valid 6-digit OTP',
          });
          return;
        }
        
        this.tfaLoading = true;
        const loginData = {
          ...this.loginForm,
          tfa_otp: this.tfaOtp
        };
        
        this.$api.sessions.login(loginData)
          .then((response) => {
            this.tfaLoading = false;
            this.tfaVerificationDialog = false;
            this.tfaOtp = '';
            this.handleSuccessfulLogin(response);
          }).catch((err) => {
            this.tfaLoading = false;
            this.$store.dispatch('setAlert', {
              status: 'error',
              show: true,
              message: err.message,
            });
          });
      },
      focusOtpInput(refName) {
        this.$nextTick(() => {
          if (this.$refs[refName]) {
            this.$refs[refName].focus();
          }
        });
      },
      copyTokenToClipboard() {
        if (this.tfaToken) {
          navigator.clipboard.writeText(this.tfaToken).then(() => {
            this.$store.dispatch('setAlert', {
              status: 'success',
              show: true,
              message: 'Secret key copied to clipboard!',
            });
          });
        }
      },
      handleSuccessfulLogin(response, customMessage = 'Logged in successfully') {
        // Set user profile in store
        this.$store.dispatch('auth/setProfile', response.profile);
        
        // Show success message
        this.$store.dispatch('setAlert', {
          status: 'success',
          show: true,
          message: customMessage,
        });
        
        // Redirect to home
        this.$router.push({name: 'home'});
      },
    },
  };
</script>

<style scoped>
  .width-login {
    width: 300px;
  }
</style>